import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AuthApi, User } from '@api';
import { ResponseError } from '@/api/runtime';
import { normalizeTimerDisplay, setTimerDisplay } from '@/utils/timerDisplay';
import { setPersona } from '@/utils/persona';
import { setRoutineStepLabels } from '@/utils/routineStepLabels';
import { setChildMode } from '@/utils/childMode';

type props = {
  children?: React.ReactNode;
};

type AuthenticationStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';
type AuthenticationError = 'forbidden' | 'unavailable' | null;

export type AppContextType = {
  user: User | null | undefined;
  setUser: (user: User | null) => void;
  authenticated: AuthenticationStatus;
  authenticationError: AuthenticationError;
  retryAuthentication: () => void;
  /**
   * Lämnar felläget och går till inloggningen. Felskärmen i AuthGuard får ALDRIG
   * vara en återvändsgränd: "Försök igen" hjälper bara den vars fel är övergående,
   * och för en app som är ett strukturstöd är "appen är borta" en betydligt värre
   * händelse än en sida som strular. Kakan rörs inte — den behövs för inloggningens
   * egen CSRF-kontroll; det är APP-tillståndet som flyttas till oautentiserat.
   */
  goToLogin: () => void;
  navbarRef: React.MutableRefObject<null | HTMLDivElement>;
  pageHeight: number;
  setPageHeight: (height: number) => void;
  showNavbar: boolean;
  setShowNavbar: (value: boolean) => void;
  // Separat scroll-driven synlighetsflagga. Hålls ISOLERAD från showNavbar
  // (som sidor som CreateActivityPage/EditActivityPage m.fl. redan sätter
  // medvetet till false för hela sidans livstid) så att scrollstopp aldrig
  // trumfar en sidas explicita "göm navbaren helt"-beslut. Faktisk synlighet
  // i Navbar räknas ut som showNavbar && scrollNavbarVisible.
  scrollNavbarVisible: boolean;
  setScrollNavbarVisible: (value: boolean) => void;
};

export const AppContext = createContext<AppContextType>({
  user: null,
  setUser: () => {},
  authenticated: 'loading',
  authenticationError: null,
  retryAuthentication: () => {},
  goToLogin: () => {},
  navbarRef: {} as React.MutableRefObject<null>,
  pageHeight: 0,
  setPageHeight: () => {},
  showNavbar: true,
  setShowNavbar: () => {},
  scrollNavbarVisible: true,
  setScrollNavbarVisible: () => {},
});

export const AppScene = ({ children }: props) => {
  const [user, setUser] = useState<User | null>();
  const [pageHeight, setPageHeight] = useState<number>(0);
  const [showNavbar, setShowNavbar] = useState<boolean>(true);
  const [scrollNavbarVisible, setScrollNavbarVisible] = useState<boolean>(true);

  const navbarRef = useRef(null);
  const [authenticated, setAuthenticated] = useState<AuthenticationStatus>(
    user ? 'authenticated' : 'loading',
  );

  const [authenticationError, setAuthenticationError] = useState<AuthenticationError>(null);
  const [attempt, setAttempt] = useState(0);
  const retryAuthentication = useCallback(() => setAttempt((value) => value + 1), []);
  const goToLogin = useCallback(() => {
    setUser(null);
    setAuthenticationError(null);
    setAuthenticated('unauthenticated');
  }, []);

  useEffect(() => {
    const request = { active: true };
    const loadUser = async () => {
      setAuthenticated('loading');
      setAuthenticationError(null);
      const xsrfToken = document.cookie.split('; ').find((row) => row.startsWith('XSRF-TOKEN='));
      if (!xsrfToken) {
        setUser(null);
        setAuthenticated('unauthenticated');
        return;
      }
      try {
        const result = await new AuthApi().getUser();
        if (!request.active) return;
        setUser(result);
        // Aktivitetsklockan ägs av handledaren och bor på kontot. Spegla serverns värde
        // till localStorage så att timern har rätt form redan vid första rendering, och
        // så att handledarens ändring slår igenom hos brukaren vid nästa inläsning.
        setTimerDisplay(
          normalizeTimerDisplay(result?.timerDisplayShape, result?.timerDisplayDigits),
        );
        // Profilen ägs av handledaren och bor på kontot (PUT /user/persona med
        // supervisedTag). Vyn läser den däremot ur localStorage: useActivePersona
        // anropar getStoredPersona(), och barnvyn gatas på childMode-flaggan. Utan den
        // här speglingen hade handledarens ändring stannat på handledarens telefon —
        // brukarens app hade fortsatt visa den persona som senast skrevs lokalt.
        //
        // Samma ordning som PersonaSelector: persona FÖRST, childMode SIST. Saknar
        // svaret en persona (äldre konto) rörs ingenting: då är den lokala flaggan det
        // enda vi har, och att nollställa den hade kastat ut brukaren ur barnvyn.
        if (result?.persona) {
          setPersona(result.tag, result.persona);
          setChildMode(result.persona === 'child' || result.persona === 'teen');
        }
        // Rutinbitarnas namn: samma ägande och samma spegling som klockan ovan.
        setRoutineStepLabels(result?.routineStepLabels === true);
        setAuthenticated(result ? 'authenticated' : 'unauthenticated');
      } catch (error) {
        if (!request.active) return;
        setAuthenticationError(
          error instanceof ResponseError && error.response?.status === 403
            ? 'forbidden'
            : 'unavailable',
        );
        setAuthenticated('error');
      }
    };
    void loadUser();
    return () => {
      request.active = false;
    };
  }, [attempt]);

  const contextProvider = useMemo(
    () => ({
      user,
      setUser,
      authenticated,
      authenticationError,
      retryAuthentication,
      goToLogin,
      navbarRef,
      pageHeight,
      setPageHeight,
      showNavbar,
      setShowNavbar,
      scrollNavbarVisible,
      setScrollNavbarVisible,
    }),
    [
      user,
      setUser,
      authenticated,
      authenticationError,
      retryAuthentication,
      goToLogin,
      navbarRef,
      pageHeight,
      setPageHeight,
      showNavbar,
      setShowNavbar,
      scrollNavbarVisible,
      setScrollNavbarVisible,
    ],
  );

  return <AppContext.Provider value={contextProvider}>{children}</AppContext.Provider>;
};
